import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  getAdminUsers,
  getJournals,
  assignJournalAdmin,
  removeJournalAdmin,
  setUserRole,
  setUserActive,
} from '../api/client';
import { useAuth } from '../App';

const ROLE_LABELS = {
  user: 'Foydalanuvchi',
  journal_admin: 'Jurnal admini',
  superadmin: 'Super admin',
};

const ROLE_COLORS = {
  user: 'bg-slate-100 text-slate-700',
  journal_admin: 'bg-blue-100 text-blue-700',
  superadmin: 'bg-amber-100 text-amber-800',
};

function RoleBadge({ role }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[role] || ROLE_COLORS.user}`}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

function AssignModal({ user, journals, onClose, onAssign }) {
  const [selectedJournalId, setSelectedJournalId] = useState('');
  const [loading, setLoading] = useState(false);

  // Filter out journals already assigned
  const assignedIds = new Set(user.journal_admin_ids || []);
  const availableJournals = journals.filter((j) => !assignedIds.has(j.id));

  const handleAssign = async () => {
    if (!selectedJournalId) return;
    setLoading(true);
    try {
      await onAssign(user.id, parseInt(selectedJournalId, 10));
      onClose();
    } catch (err) {
      // handled in parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Jurnal admini tayinlash</h3>
        <p className="text-sm text-slate-500 mb-4">
          {user.last_name} {user.first_name} ({user.email})
        </p>

        {availableJournals.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Barcha jurnallarga allaqachon biriktirilgan</p>
        ) : (
          <div className="space-y-3">
            <select
              value={selectedJournalId}
              onChange={(e) => setSelectedJournalId(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">Jurnalni tanlang...</option>
              {availableJournals.map((j) => (
                <option key={j.id} value={j.id}>{j.name} ({j.slug})</option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
                Bekor qilish
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedJournalId || loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Tayinlanmoqda...' : 'Tayinlash'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UserRow({ user, journals, onAssign, onRemoveJournal, onToggleActive, onChangeRole, currentUserId }) {
  const [expanded, setExpanded] = useState(false);
  const userJournals = journals.filter((j) => (user.journal_admin_ids || []).includes(j.id));
  const isSelf = user.id === currentUserId;

  return (
    <>
      <tr
        className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-3 text-sm text-slate-500 font-mono">{user.id}</td>
        <td className="px-4 py-3">
          <div className="text-sm font-medium text-slate-900">{user.last_name} {user.first_name}</div>
          <div className="text-xs text-slate-400">{user.email}</div>
        </td>
        <td className="px-4 py-3"><RoleBadge role={user.role} /></td>
        <td className="px-4 py-3">
          {userJournals.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {userJournals.map((j) => (
                <span key={j.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {j.slug}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-slate-300">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
            {user.is_active ? 'Faol' : 'Nofaol'}
          </span>
        </td>
        <td className="px-4 py-3 text-right">
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/80">
          <td colSpan={6} className="px-4 py-4">
            <div className="flex flex-wrap gap-2 items-center">
              {/* Assign journal admin */}
              <button
                onClick={() => onAssign(user)}
                className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                + Jurnal tayinlash
              </button>

              {/* Remove journal admin badges */}
              {userJournals.map((j) => (
                <button
                  key={j.id}
                  onClick={() => onRemoveJournal(user.id, j.id, j.slug)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                  title={`${j.slug} adminligini olib tashlash`}
                >
                  {j.slug} <span className="text-red-400">&times;</span>
                </button>
              ))}

              <span className="w-px h-5 bg-slate-200 mx-1" />

              {/* Toggle active */}
              {!isSelf && (
                <button
                  onClick={() => onToggleActive(user.id, !user.is_active)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    user.is_active
                      ? 'text-orange-700 bg-orange-50 border-orange-200 hover:bg-orange-100'
                      : 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                  }`}
                >
                  {user.is_active ? 'Bloklash' : 'Faollashtirish'}
                </button>
              )}

              {/* Role change */}
              {!isSelf && user.role !== 'superadmin' && (
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) onChangeRole(user.id, e.target.value);
                  }}
                  className="px-2 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="">Rolni o'zgartirish...</option>
                  {user.role !== 'user' && <option value="user">Foydalanuvchi</option>}
                  {user.role !== 'journal_admin' && <option value="journal_admin">Jurnal admini</option>}
                  <option value="superadmin">Super admin</option>
                </select>
              )}

              {isSelf && (
                <span className="text-xs text-slate-400 italic ml-2">(Bu siz)</span>
              )}
            </div>

            {user.last_login_at && (
              <p className="text-xs text-slate-400 mt-2">
                Oxirgi kirish: {new Date(user.last_login_at).toLocaleString('uz-UZ')}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function SuperUsersPage() {
  const { isSuperAdmin, admin } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [journals, setJournals] = useState([]);
  const [assignUser, setAssignUser] = useState(null);
  const searchTimerRef = useRef(null);
  const pageSize = 20;

  const fetchUsers = useCallback(async (p = page, s = search, r = roleFilter) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: pageSize };
      if (s.trim()) params.search = s.trim();
      if (r) params.role = r;
      const res = await getAdminUsers(params);
      setUsers(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Foydalanuvchilarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter]);

  useEffect(() => {
    fetchUsers(page, search, roleFilter);
  }, [page, roleFilter, fetchUsers]);

  useEffect(() => {
    getJournals({ active_only: false })
      .then((res) => setJournals(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const handleSearchChange = (value) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      fetchUsers(1, value, roleFilter);
    }, 300);
  };

  const handleAssign = async (userId, journalId) => {
    try {
      await assignJournalAdmin(userId, journalId);
      toast.success('Jurnal admini tayinlandi');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tayinlashda xatolik');
      throw err;
    }
  };

  const handleRemoveJournal = async (userId, journalId, slug) => {
    if (!window.confirm(`${slug} jurnali adminligini olib tashlashni xohlaysizmi?`)) return;
    try {
      await removeJournalAdmin(userId, journalId);
      toast.success(`${slug} adminligi olib tashlandi`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Olib tashlashda xatolik');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await setUserActive(userId, isActive);
      toast.success(isActive ? 'Foydalanuvchi faollashtirildi' : 'Foydalanuvchi bloklandi');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    }
  };

  const handleChangeRole = async (userId, role) => {
    const label = ROLE_LABELS[role] || role;
    if (!window.confirm(`Rolni "${label}" ga o'zgartirmoqchimisiz?`)) return;
    try {
      await setUserRole(userId, role);
      toast.success(`Rol "${label}" ga o'zgartirildi`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-slate-400">Bu sahifa faqat super admin uchun.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">Foydalanuvchilar boshqaruvi</h1>
        <p className="text-sm text-slate-400 mt-1">
          Jami {total} foydalanuvchi
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Email yoki ism bo'yicha qidirish..."
          className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
        />
        <select
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">Barcha rollar</option>
          <option value="user">Foydalanuvchi</option>
          <option value="journal_admin">Jurnal admini</option>
          <option value="superadmin">Super admin</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">F.I.Sh / Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Rol</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Jurnallar</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Holat</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading && users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-700 border-t-transparent mx-auto" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400">
                    Foydalanuvchilar topilmadi
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    journals={journals}
                    onAssign={setAssignUser}
                    onRemoveJournal={handleRemoveJournal}
                    onToggleActive={handleToggleActive}
                    onChangeRole={handleChangeRole}
                    currentUserId={admin?.id}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
            <p className="text-xs text-slate-500">
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} / {total}
            </p>
            <div className="flex gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="px-3 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                &larr;
              </button>
              <span className="px-3 py-1 text-xs font-medium text-slate-700">
                {page} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                &rarr;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Assign modal */}
      {assignUser && (
        <AssignModal
          user={assignUser}
          journals={journals}
          onClose={() => setAssignUser(null)}
          onAssign={handleAssign}
        />
      )}
    </div>
  );
}
